export { TaskOverviewSortingFieldsSchema } from '@einwert/valuation-api-client'
import { z } from 'zod'
import { dayjsOrNull, OrderStatusSchema } from 'ComponentLibrary/src/common/schemas.zod'
import { LiteralUnion } from 'type-fest'
import { SUPPORTED_LOCALES } from 'ClientPortal/src/i18n/supportedLocales'

export const DateRangeSchema = z.object({
    start: dayjsOrNull,
    end: dayjsOrNull,
})

export const PortfolioMetadataSchema = z.object({
    uuid: z.string(),
    title: z.string(),
})

export const AssignedCustomersSchema = z.array(z.number())
export const OrderStatusesSchema = z.array(OrderStatusSchema)
export const TitleFilterSchema = z.string()

export const OrderListFilterStateSchema = z.object({
    statuses: OrderStatusesSchema.default([]),
    assignedCustomers: AssignedCustomersSchema.default([]),
    titleFilter: TitleFilterSchema.default(''),
    valuationDateRange: DateRangeSchema.default({
        start: null,
        end: null,
    }),
    portfolio: PortfolioMetadataSchema.optional(),
})

const UuidSchema = z.string()

export const notificationPrioritySchema = z.union([z.literal('do_not_send'), z.literal('batch'), z.literal('immediate')])

export const BaseUserSchema = z.object({
    uuid: z.string(),
    id: z.number(),
    organization_name: z.string(),
    salutation: z.string().optional(),
    title: z.string().optional(),
    job_title: z.string().optional(),
    phone_number: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string(),
    profile_image_url: z.string().optional(),
    profile_thumbnail_image_url: z.string().optional(),
    pinned_threads: z.array(UuidSchema),
    is_platform_user: z.boolean().optional(),
    isCurrentUser: z.boolean().optional(), // This is set in the frontend to identify the current user in lists
    locale: z.enum(SUPPORTED_LOCALES).optional(),
})

export const AssignedAppraiserSchema = z.object({
    id: z.number(),
    organization_name: z.string(),
    salutation: z.string().optional(),
    email: z.string(),
    phone_number: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    certification: z.string().optional(),
    profile_image_url: z.string().optional(),
})

export const NotificationSettingsSchema = z.object({
    new_message_in_thread_priority: notificationPrioritySchema.optional(),
    new_message_in_thread_with_mention_priority: notificationPrioritySchema.optional(),
    new_message_in_thread_connected_to_my_task_priority: notificationPrioritySchema.optional(),
    new_message_in_thread_connected_to_task_priority: notificationPrioritySchema.optional(),
    new_task_assignee_in_my_task_priority: notificationPrioritySchema.optional(),
    order_blocked_priority: notificationPrioritySchema.optional(),
    valuation_finalised_priority: notificationPrioritySchema.optional(),
    order_accepted_priority: notificationPrioritySchema.optional(),
    new_order_due_date_priority: notificationPrioritySchema.optional(),
    new_inspection_date_priority: notificationPrioritySchema.optional(),
    task_created_priority: notificationPrioritySchema.optional(),
    my_task_status_transitioned_priority: notificationPrioritySchema.optional(),
    task_status_transitioned_priority: notificationPrioritySchema.optional(),
    task_due_tomorrow_priority: notificationPrioritySchema.optional(),
    task_due_soon_priority: notificationPrioritySchema.optional(),
    order_document_attached_priority: notificationPrioritySchema.optional(),
    order_status_transitioned_priority: notificationPrioritySchema.optional(),
    draft_valuation_created_priority: notificationPrioritySchema.optional(),
    value_check_finalised_priority: notificationPrioritySchema.optional(),
    plausibility_handed_over_priority: notificationPrioritySchema.optional(),
    plausibility_new_message_priority: notificationPrioritySchema.optional(),
    plausibility_edited_message_priority: notificationPrioritySchema.optional(),
    plausibility_deleted_message_priority: notificationPrioritySchema.optional(),
    plausibility_internally_approved_priority: notificationPrioritySchema.optional(),
    plausibility_approved_priority: notificationPrioritySchema.optional(),
})

export const AppraiserUserSchema = z
    .object({
        role: z.literal('appraiser'),
    })
    .and(BaseUserSchema)

export const CustomerUserSchema = z
    .object({
        role: z.literal('customer'),
        customer_uuid: UuidSchema,
        team_uuid: UuidSchema.optional(),
        notification_settings: NotificationSettingsSchema,
    })
    .and(BaseUserSchema)

const zodLiteralUnion = <T extends U, U extends string>(_: readonly T[]): z.ZodType<LiteralUnion<T, string>> => {
    return z.string() as z.ZodType<LiteralUnion<T, string>>
}

const TaskStatusSchema = zodLiteralUnion(['open', 'done'] as const)

export const TaskOverviewPageUrlStateSchema = z.object({
    order: z.object({ id: z.string(), label: z.string() }).nullable().optional().default(null),
    status: TaskStatusSchema.optional().default('open'),
    assignee: BaseUserSchema.nullable().optional().default(null),
    team: z.object({ id: z.string(), label: z.string() }).nullable().optional().default(null),
    portfolio: z.object({ id: z.string(), label: z.string() }).nullable().optional().default(null),
    searchString: z.string().default(''),
    sortBy: z.union([z.literal('+due_date'), z.literal('-due_date'), z.literal('+title'), z.literal('-title')]).default('+due_date'),
})

export const ThreadOverviewPageUrlStateSchema = z.object({
    orderFilterValue: z.string().nullable().default(null),
})
